import { useEffect, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold, Italic, Underline as UnderlineIcon,
  Strikethrough, List, ListOrdered, Heading2,
} from "lucide-react";

interface RichTextEditorProps {
  value: string;
  onChange: (value: string, formatted: any) => void;
  placeholder?: string;
  rows?: number;
}

export function RichTextEditor({ value, onChange, placeholder }: RichTextEditorProps) {
  // Keep a ref so TipTap's onUpdate always calls the latest onChange,
  // not the stale one captured at editor creation time.
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; });

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Placeholder.configure({ placeholder: placeholder ?? "Escreva o conteúdo do post..." }),
    ],
    content: value || "",
    onUpdate({ editor }) {
      const html = editor.getHTML();
      onChangeRef.current(html, editor.getJSON());
    },
    editorProps: {
      attributes: {
        class: "min-h-[280px] px-4 py-3 outline-none text-sm text-gray-800 leading-relaxed",
      },
    },
  });

  // Sync external value changes (e.g. template applied)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== current) {
      editor.commands.setContent(value || "", false);
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!editor) return null;

  const btn = (
    active: boolean,
    action: () => void,
    icon: React.ReactNode,
    label: string,
  ) => (
    <button
      type="button"
      title={label}
      onMouseDown={(e) => { e.preventDefault(); action(); }}
      className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all ${
        active
          ? "bg-brand-primary text-white shadow-sm"
          : "text-gray-600 hover:bg-white hover:text-gray-900 hover:shadow-sm"
      }`}
    >
      {icon}
    </button>
  );

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-brand-primary/30 focus-within:border-brand-primary transition-shadow">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-gray-100 bg-gray-50 flex-wrap">
        {btn(editor.isActive("bold"),        () => editor.chain().focus().toggleBold().run(),        <Bold className="h-4 w-4" />,          "Negrito")}
        {btn(editor.isActive("italic"),      () => editor.chain().focus().toggleItalic().run(),      <Italic className="h-4 w-4" />,        "Itálico")}
        {btn(editor.isActive("underline"),   () => editor.chain().focus().toggleUnderline().run(),   <UnderlineIcon className="h-4 w-4" />, "Sublinhado")}
        {btn(editor.isActive("strike"),      () => editor.chain().focus().toggleStrike().run(),      <Strikethrough className="h-4 w-4" />, "Tachado")}
        <div className="w-px h-5 bg-gray-300 mx-1" />
        {btn(editor.isActive("bulletList"),  () => editor.chain().focus().toggleBulletList().run(),  <List className="h-4 w-4" />,          "Lista")}
        {btn(editor.isActive("orderedList"), () => editor.chain().focus().toggleOrderedList().run(), <ListOrdered className="h-4 w-4" />,   "Lista numerada")}
        {btn(editor.isActive("heading", { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), <Heading2 className="h-4 w-4" />, "Título")}
      </div>

      {/* WYSIWYG area */}
      <EditorContent editor={editor} />
    </div>
  );
}
